/**
 * 業務フロー・ステージの図（F2）のルートパス定義。業務フローの図、案件から見た業務フロー、
 * ステージの図と、業務マップへ倒す旧 URL。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図・案件から見た業務フロー・ステージの図・旧ストーリーの URL のルートパスを定義する。
 * @graph-connects none
 */
import type { FeatureRouteSets } from './feature-route-sets.js';

/**
 * 業務フロー 1 本をパスに置くルートで、業務フローの id が入るパスパラメータの名前。
 *
 * この名前は 2 本のルート（`FLOW_ROUTE` と `PJ_FLOW_ROUTE`）に現れ、**PJ を外す行き先を組む側が
 * その 2 本を繋ぐ**（`pjExitTarget`）。直に書くとルートの綴りを変えたときに黙って読めなくなるので
 * 定数に置き、雛形がこの名前で終わることをテストで突き合わせる。
 * @graph-connects none
 */
export const FLOW_PATH_PARAM = 'flowId';

/** 業務フローの図。 @graph-connects none */
export const FLOW_ROUTE = '/flows/$flowId';

/**
 * PJ 1 件から見た業務フロー 1 本（変更前 / 変更後の比較）。
 *
 * PJ をパスの親に置くのは、PJ が画面より上位の軸だからである。パスに置けば、PJ の無いこの画面は
 * URL として作れない。
 * @graph-connects none
 */
export const PJ_FLOW_ROUTE = '/pjs/$pjNumber/flows/$flowId';

/** ステージの図。案件は検索条件（`?pj=`）で受ける。 @graph-connects none */
export const STAGE_ROUTE = '/stages/$stageId';

/**
 * 旧ユーザーストーリー図。業務マップへ倒すだけのルートである。
 *
 * ストーリーは業務フローに置き換わり、番号の空間も変わった。旧 id から業務フローを引く対応は
 * 無いので、ブックマークを 404 にせず、業務フローを辿れる業務マップへ送る。
 * @graph-connects none
 */
export const USER_STORY_DETAIL_ROUTE = '/user-stories/$usId';

/**
 * 業務フローのルートの集合。業務フローの 2 本は左右比較の開閉を検索条件で受け、ステージの図は
 * 案件を検索条件で受ける。旧ストーリーの倒し先は検索条件を検証しない。
 * @graph-connects none
 */
export const flowRouteSets = {
  all: [FLOW_ROUTE, PJ_FLOW_ROUTE, STAGE_ROUTE, USER_STORY_DETAIL_ROUTE],
  pjSearch: [STAGE_ROUTE],
  operationSearch: [FLOW_ROUTE, PJ_FLOW_ROUTE],
  pjPath: [PJ_FLOW_ROUTE],
} as const satisfies FeatureRouteSets;
